import type {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'material-react-table';

interface ParamOptions {
  initialColumnFilters?: MRT_ColumnFiltersState;
  initialGlobalFilter?: string;
  initialPageSize?: number;
  initialSorting?: MRT_SortingState;
  paramPrefix?: string;
}

interface BuildParamOptions extends ParamOptions {
  columnFilters: MRT_ColumnFiltersState;
  defaultPageSize: number;
  globalFilter?: string;
  pagination: MRT_PaginationState;
  sorting: MRT_SortingState;
}

interface RememberTableParamNames {
  filtersParam: string;
  globalFilterParam: string;
  limitParam: string;
  pageParam: string;
  sortParam: string;
}

const DEFAULT_FILTERS_PARAM = 'filters';
const DEFAULT_GLOBAL_FILTER_PARAM = 'q';
const DEFAULT_LIMIT_PARAM = 'limit';
const DEFAULT_PAGE_PARAM = 'page';
const DEFAULT_SORT_PARAM = 'sort';

const FILTER_PAIR_SEPARATOR = ',';
const FILTER_KEY_VALUE_SEPARATOR = ':';
const SORT_PAIR_SEPARATOR = ',';
const SORT_KEY_VALUE_SEPARATOR = ':';

const buildParamName = (prefix: string | undefined, base: string) =>
  prefix ? `${prefix}${base[0].toUpperCase()}${base.slice(1)}` : base;

const getParamNames = (paramPrefix?: string): RememberTableParamNames => {
  const trimmedPrefix = paramPrefix?.trim();

  return {
    filtersParam: buildParamName(trimmedPrefix, DEFAULT_FILTERS_PARAM),
    globalFilterParam: buildParamName(
      trimmedPrefix,
      DEFAULT_GLOBAL_FILTER_PARAM
    ),
    limitParam: buildParamName(trimmedPrefix, DEFAULT_LIMIT_PARAM),
    pageParam: buildParamName(trimmedPrefix, DEFAULT_PAGE_PARAM),
    sortParam: buildParamName(trimmedPrefix, DEFAULT_SORT_PARAM),
  };
};

const isPrimitiveFilterValue = (
  value: unknown
): value is string | number | boolean =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

const isColumnFilter = (
  filter: {
    id: string;
    value: MRT_ColumnFiltersState[number]['value'];
  } | null
): filter is {
  id: string;
  value: MRT_ColumnFiltersState[number]['value'];
} => filter !== null;

const parseReadableFilters = (
  encodedFilters: string
): MRT_ColumnFiltersState => {
  const parts = encodedFilters
    .split(FILTER_PAIR_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);

  return parts
    .map((part) => {
      const separatorIndex = part.indexOf(FILTER_KEY_VALUE_SEPARATOR);
      if (separatorIndex === -1) {
        return null;
      }
      const id = decodeURIComponent(part.slice(0, separatorIndex));
      const rawValue = decodeURIComponent(part.slice(separatorIndex + 1));

      if (!id) {
        return null;
      }

      return {
        id,
        value: rawValue,
      };
    })
    .filter(isColumnFilter);
};

const buildReadableFiltersParam = (
  filters: MRT_ColumnFiltersState
): string | null => {
  if (!filters.every((filter) => isPrimitiveFilterValue(filter.value))) {
    return null;
  }

  return filters
    .map(
      (filter) =>
        `${encodeURIComponent(filter.id)}${FILTER_KEY_VALUE_SEPARATOR}${encodeURIComponent(
          String(filter.value)
        )}`
    )
    .join(FILTER_PAIR_SEPARATOR);
};

const parseNonNegativeInteger = (value: string | null, fallback: number) => {
  if (value === null) {
    return fallback;
  }

  const parsed = Number(value);

  if (!Number.isInteger(parsed) || parsed < 0) {
    return fallback;
  }

  return parsed;
};

const parsePositiveInteger = (value: string | null, fallback: number) => {
  const parsed = parseNonNegativeInteger(value, fallback);

  if (parsed <= 0) {
    return fallback;
  }

  return parsed;
};

const parseColumnFilters = (
  params: URLSearchParams,
  filtersParam: string,
  fallback: MRT_ColumnFiltersState
) => {
  const encodedFilters = params.get(filtersParam);

  if (!encodedFilters) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(encodedFilters) as MRT_ColumnFiltersState;

    if (Array.isArray(parsed)) {
      return parsed.filter((filter) => filter && typeof filter.id === 'string');
    }
  } catch {
    const parsedReadable = parseReadableFilters(encodedFilters);
    if (parsedReadable.length) {
      return parsedReadable;
    }
  }

  return fallback;
};

const parseColumnFilterParams = (
  params: URLSearchParams,
  paramNames: RememberTableParamNames,
  options: ParamOptions
) =>
  parseColumnFilters(
    params,
    paramNames.filtersParam,
    options.initialColumnFilters ?? []
  );

const buildColumnFilterParams = (
  params: URLSearchParams,
  paramNames: RememberTableParamNames,
  columnFilters: MRT_ColumnFiltersState
) => {
  const sanitizedFilters = sanitizeFilters(columnFilters);

  if (sanitizedFilters.length) {
    const readableFilters = buildReadableFiltersParam(sanitizedFilters);
    params.set(
      paramNames.filtersParam,
      readableFilters ?? JSON.stringify(sanitizedFilters)
    );
  } else {
    params.delete(paramNames.filtersParam);
  }
};

const parseSorting = (
  params: URLSearchParams,
  sortParam: string,
  fallback: MRT_SortingState
) => {
  const encodedSort = params.get(sortParam);

  if (!encodedSort) {
    return fallback;
  }

  const parsedSorting = encodedSort
    .split(SORT_PAIR_SEPARATOR)
    .map((part) => {
      const separatorIndex = part.lastIndexOf(SORT_KEY_VALUE_SEPARATOR);
      if (separatorIndex === -1) {
        return null;
      }

      const id = decodeURIComponent(part.slice(0, separatorIndex));
      const direction = part.slice(separatorIndex + 1);

      if (!id || (direction !== 'asc' && direction !== 'desc')) {
        return null;
      }

      return {
        id,
        desc: direction === 'desc',
      };
    })
    .filter(
      (sort): sort is MRT_SortingState[number] =>
        sort !== null && typeof sort.id === 'string'
    );

  return parsedSorting.length ? parsedSorting : fallback;
};

const parseSortingParams = (
  params: URLSearchParams,
  paramNames: RememberTableParamNames,
  options: ParamOptions
) => parseSorting(params, paramNames.sortParam, options.initialSorting ?? []);

const sanitizeFilters = (columnFilters: MRT_ColumnFiltersState) =>
  columnFilters.filter((filter) => {
    const value = filter.value;
    return (
      value !== undefined && value !== null && value !== '' && value !== ','
    );
  });

const buildSortingParam = (sorting: MRT_SortingState) =>
  sorting
    .filter((sort) => sort.id)
    .map(
      (sort) =>
        `${encodeURIComponent(sort.id)}${SORT_KEY_VALUE_SEPARATOR}${
          sort.desc ? 'desc' : 'asc'
        }`
    )
    .join(SORT_PAIR_SEPARATOR);

const sortingStatesEqual = (
  sorting: MRT_SortingState,
  defaultSorting: MRT_SortingState
) => buildSortingParam(sorting) === buildSortingParam(defaultSorting);

const buildSortingParams = (
  params: URLSearchParams,
  paramNames: RememberTableParamNames,
  sorting: MRT_SortingState,
  defaultSorting: MRT_SortingState
) => {
  const sortingParam = buildSortingParam(sorting);
  if (sortingParam && !sortingStatesEqual(sorting, defaultSorting)) {
    params.set(paramNames.sortParam, sortingParam);
  } else {
    params.delete(paramNames.sortParam);
  }
};

const parsePaginationParams = (
  params: URLSearchParams,
  paramNames: RememberTableParamNames,
  defaultPageSize: number
): MRT_PaginationState => ({
  pageIndex: parseNonNegativeInteger(params.get(paramNames.pageParam), 0),
  pageSize: parsePositiveInteger(
    params.get(paramNames.limitParam),
    defaultPageSize
  ),
});

const buildPaginationParams = (
  params: URLSearchParams,
  paramNames: RememberTableParamNames,
  pagination: MRT_PaginationState,
  defaultPageSize: number
) => {
  if (pagination.pageIndex !== 0) {
    params.set(paramNames.pageParam, pagination.pageIndex.toString());
  } else {
    params.delete(paramNames.pageParam);
  }

  if (pagination.pageSize !== defaultPageSize) {
    params.set(paramNames.limitParam, pagination.pageSize.toString());
  } else {
    params.delete(paramNames.limitParam);
  }
};

const parseGlobalFilterParam = (
  params: URLSearchParams,
  paramNames: RememberTableParamNames,
  fallback = ''
) => params.get(paramNames.globalFilterParam) ?? fallback;

const buildGlobalFilterParam = (
  params: URLSearchParams,
  paramNames: RememberTableParamNames,
  globalFilterValue?: string
) => {
  const globalFilter = globalFilterValue?.trim() ?? '';

  if (globalFilter) {
    params.set(paramNames.globalFilterParam, globalFilter);
  } else {
    params.delete(paramNames.globalFilterParam);
  }
};

export const parseRememberTableParams = (
  params: URLSearchParams,
  options: ParamOptions = {}
) => {
  const paramNames = getParamNames(options.paramPrefix);
  const defaultPageSize = options.initialPageSize ?? 20;

  return {
    columnFilters: parseColumnFilterParams(params, paramNames, options),
    globalFilter: parseGlobalFilterParam(
      params,
      paramNames,
      options.initialGlobalFilter
    ),
    pagination: parsePaginationParams(params, paramNames, defaultPageSize),
    sorting: parseSortingParams(params, paramNames, options),
  };
};

export const buildRememberTableParams = (
  currentParams: URLSearchParams,
  options: BuildParamOptions
) => {
  const newParams = new URLSearchParams(currentParams);
  const paramNames = getParamNames(options.paramPrefix);

  buildColumnFilterParams(newParams, paramNames, options.columnFilters);
  buildPaginationParams(
    newParams,
    paramNames,
    options.pagination,
    options.defaultPageSize
  );
  buildSortingParams(
    newParams,
    paramNames,
    options.sorting,
    options.initialSorting ?? []
  );
  buildGlobalFilterParam(newParams, paramNames, options.globalFilter);

  return newParams;
};
